import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoggerService } from '../../../../../services/logger-services/logger.service';
import { AgentApiService } from '../../../../../shared/services/agent-api/agent-api.service';
import { ApiService } from '../../../../../services/api/api.service';
import { TokenService } from '../../../../../shared/services/token/token.service';
import {
  asSavedAnswerMetadata,
  citationsToNumberedMetadata,
  hasSourceReferences,
  isRegulationsAgent
} from '../../regulation-citation/regulation-citation.util';

const GET_SHARED_THREAD_KEY = 'get_shared_thread';
const GET_SHARED_CONVERSATION_KEY = 'get_shared_conversation';
const VIA_AGENT_ID = 'via_agent';

/**
 * Get Shared Chat Service
 *
 * Handles fetching shared conversation data (both full threads and single responses)
 * using agent-specific endpoints and base URLs.
 *
 * Features:
 * - Agent-specific routing (Via, regulations, and other agents)
 * - Supports both thread sharing and single response sharing
 * - JWT token-based authentication
 * - Error handling and logging
 */
@Injectable({
  providedIn: 'root'
})
export class GetSharedChatService {
  constructor(
    private readonly logger: LoggerService,
    private readonly agentApiService: AgentApiService,
    private readonly apiService: ApiService,
    private readonly tokenService: TokenService
  ) {}

  /**
   * Fetch shared thread data using share link token
   *
   * @param agentId - Agent identifier
   * @param tenantId - Tenant identifier
   * @param threadId - Thread identifier
   * @param shareToken - JWT share link token
   * @param userId - Optional user identifier (required for Via agent)
   * @returns Observable<any> - Shared thread data
   */
  getSharedThread(
    agentId: string,
    tenantId: string,
    threadId: string,
    shareToken: string,
    userId?: string
  ): Observable<any> {
    try {
      const endpoint = this.agentApiService.getApiUrlFromConfig(GET_SHARED_THREAD_KEY, agentId);
      if (!endpoint) {
        this.logger.logError(
          `[GetSharedChat] No get_shared_thread endpoint configured for agent: ${agentId}`,
          'getSharedThread'
        );
        return of({ status: 404, error: 'Endpoint not configured' });
      }

      const usesCentralizedApi = this.agentApiService.usesCommonEndpoint(GET_SHARED_THREAD_KEY, agentId);

      if (usesCentralizedApi) {
        // Centralized view_share_thread only needs tenant_id + share_link
        const centralBaseUrl = this.agentApiService.getBaseUrlForEndpoint(GET_SHARED_THREAD_KEY, agentId);

        this.logger.logInfo(
          `[GetSharedChat] ${agentId} – fetching shared thread (centralized) at ${centralBaseUrl}${endpoint}`,
          'getSharedThread'
        );

        return this.agentApiService
          .post<any>(endpoint, { tenant_id: tenantId, share_link: shareToken }, undefined, centralBaseUrl)
          .pipe(
            map((response: any) => this.wrapResponseForCompatibility(response, agentId, true)),
            catchError((error: any) => {
              this.logger.logError(
                `[GetSharedChat] ${agentId} shared thread fetch failed: ${error?.message || error}`,
                'getSharedThread'
              );
              return of({ status: error?.status || 500, error: error?.message || 'Failed to fetch shared thread' });
            })
          );
      }

      const customBaseUrl = this.agentApiService.getBaseUrlForAgent(agentId);
      const requestBody: any = {
        tenant_id: tenantId,
        thread_id: threadId,
        share_link: shareToken
      };

      // Legacy Via agent requires user_id in the request body (use provided or empty string)
      if (agentId === VIA_AGENT_ID) {
        requestBody.user_id = userId || '';
      }

      this.logger.logInfo(
        `[GetSharedChat] ${agentId} – fetching shared thread at ${customBaseUrl}${endpoint} with body: ${JSON.stringify(requestBody)}`,
        'getSharedThread'
      );

      return this.apiService
        .post<any>(endpoint, requestBody, this.tokenService.getAccessToken(), 'COMMON', customBaseUrl)
        .pipe(
          map((response: any) => this.wrapResponseForCompatibility(response, agentId, false)),
          catchError((error: any) => {
            this.logger.logError(
              `[GetSharedChat] ${agentId} shared thread fetch failed: ${error?.message || error}`,
              'getSharedThread'
            );
            return of({ status: error?.status || 500, error: error?.message || 'Failed to fetch shared thread' });
          })
        );
    } catch (error: any) {
      this.logger.logError(
        `[GetSharedChat] Exception in getSharedThread: ${error?.message || error}`,
        'getSharedThread'
      );
      return of({ status: 500, error: 'Internal error' });
    }
  }

  /**
   * Fetch shared conversation (single response) data using share link token
   *
   * @param agentId - Agent identifier
   * @param tenantId - Tenant identifier
   * @param responseId - Response identifier
   * @param shareToken - JWT share link token
   * @param userId - Optional user identifier (required for Via agent)
   * @returns Observable<any> - Shared conversation data
   */
  getSharedConversation(
    agentId: string,
    tenantId: string,
    responseId: string,
    shareToken: string,
    userId?: string
  ): Observable<any> {
    try {
      const endpoint = this.agentApiService.getApiUrlFromConfig(GET_SHARED_CONVERSATION_KEY, agentId);
      if (!endpoint) {
        this.logger.logError(
          `[GetSharedChat] No get_shared_conversation endpoint configured for agent: ${agentId}`,
          'getSharedConversation'
        );
        return of({ status: 404, error: 'Endpoint not configured' });
      }

      const usesCentralizedApi = this.agentApiService.usesCommonEndpoint(GET_SHARED_CONVERSATION_KEY, agentId);

      if (usesCentralizedApi) {
        // Centralized view_share_conversation only needs tenant_id + share_link;
        // the message_id is read from the share token by the backend
        const centralBaseUrl = this.agentApiService.getBaseUrlForEndpoint(GET_SHARED_CONVERSATION_KEY, agentId);

        this.logger.logInfo(
          `[GetSharedChat] ${agentId} – fetching shared conversation (centralized) at ${centralBaseUrl}${endpoint}`,
          'getSharedConversation'
        );

        return this.agentApiService
          .post<any>(endpoint, { tenant_id: tenantId, share_link: shareToken }, undefined, centralBaseUrl)
          .pipe(
            map((response: any) => this.wrapResponseForCompatibility(response, agentId, true)),
            catchError((error: any) => {
              this.logger.logError(
                `[GetSharedChat] ${agentId} shared conversation fetch failed: ${error?.message || error}`,
                'getSharedConversation'
              );
              return of({ status: error?.status || 500, error: error?.message || 'Failed to fetch shared conversation' });
            })
          );
      }

      const customBaseUrl = this.agentApiService.getBaseUrlForAgent(agentId);
      const requestBody: any = {
        tenant_id: tenantId,
        response_id: responseId,
        share_link: shareToken
      };

      // Legacy Via agent requires user_id in the request body (use provided or empty string)
      if (agentId === VIA_AGENT_ID) {
        requestBody.user_id = userId || '';
      }

      this.logger.logInfo(
        `[GetSharedChat] ${agentId} – fetching shared conversation at ${customBaseUrl}${endpoint} with body: ${JSON.stringify(requestBody)}`,
        'getSharedConversation'
      );

      return this.apiService
        .post<any>(endpoint, requestBody, this.tokenService.getAccessToken(), 'COMMON', customBaseUrl)
        .pipe(
          map((response: any) => this.wrapResponseForCompatibility(response, agentId, false)),
          catchError((error: any) => {
            this.logger.logError(
              `[GetSharedChat] ${agentId} shared conversation fetch failed: ${error?.message || error}`,
              'getSharedConversation'
            );
            return of({ status: error?.status || 500, error: error?.message || 'Failed to fetch shared conversation' });
          })
        );
    } catch (error: any) {
      this.logger.logError(
        `[GetSharedChat] Exception in getSharedConversation: ${error?.message || error}`,
        'getSharedConversation'
      );
      return of({ status: 500, error: 'Internal error' });
    }
  }

  /**
   * Wrap API response in a compatibility layer for legacy code
   * Ensures response has a consistent structure with status and body
   *
   * Handles different response formats:
   * - Centralized agent-common API: { status: "Success", data: [...] } keyed by message_id
   *   (needs transformation via transformCentralizedSharedResponse)
   * - Agent-owned API returning a flat array (e.g. the regulations chatbot v2 endpoints):
   *   { status: "Success", data: [...], thread_name: "..." } — already in the legacy
   *   shape, so it only needs wrapping
   * - Via agent: { status: "success", data: { responses: [...], thread_name: "..." } }
   *   (needs transformation via transformPacAgentResponse)
   * - Other agents: Similar to Via format but may have variations
   *   (needs transformation via transformGenericAgentResponse)
   *
   * @param response - Raw API response
   * @param agentId - Agent identifier to determine transformation strategy
   * @param usesCentralizedApi - True when the response came from the centralized
   *                             agent-common API (flat `data` array keyed by `message_id`)
   * @returns Wrapped response with status 200 and body containing data
   */
  private wrapResponseForCompatibility(response: any, agentId?: string, usesCentralizedApi = false): any {
    // If response already has numeric status and body structure, return as-is
    if (typeof response?.status === 'number' && response?.body) {
      return response;
    }

    const isSuccess = response?.status === 'Success' || response?.status === 'success';
    const hasFlatData = isSuccess && Array.isArray(response?.data);

    // Centralized API (RIA, VIA on agent-common): flat data array keyed by message_id
    if (usesCentralizedApi && hasFlatData) {
      return this.transformCentralizedSharedResponse(response, agentId);
    }

    // Agent-owned API already returning the legacy flat shape: just wrap it
    if (hasFlatData) {
      return {
        status: 200,
        body: {
          thread_name: response.thread_name || '',
          data: response.data
        }
      };
    }

    // Legacy Via agent: needs transformation
    if (!usesCentralizedApi && agentId === VIA_AGENT_ID && isSuccess && response?.data?.responses) {
      return this.transformPacAgentResponse(response);
    }

    // Other agents: attempt generic transformation
    if (response?.status === 'success' && response?.data?.responses) {
      return this.transformGenericAgentResponse(response);
    }

    // Fallback: wrap the response in the expected format
    return {
      status: 200,
      body: response?.data || response
    };
  }

  /**
   * Transform centralized shared response (RIA, future agents) to expected format.
   *
   * Centralized API returns data in display order (chronological). The share viewer
   * component applies .reverse() assuming newest-first, so we pre-reverse here to
   * counteract it and preserve the backend's intended display order.
   *
   * Centralized API response structure:
   * {
   *   status: "Success",
   *   message: "...",
   *   thread_name: "...",
   *   supervisor: [...],
   *   data: [{ message_id, question, answer: { text: "..." }, agent_response_created_at, ... }],
   *   thread_id: "..."
   * }
   */
  private transformCentralizedSharedResponse(response: any, agentId?: string): any {
    const rawItems: any[] = Array.isArray(response.data) ? response.data : [];

    const transformedData = rawItems.map((item: any) => {
      let answerText = '';
      if (item.answer && typeof item.answer === 'object') {
        answerText = item.answer.text || item.answer.answer || '';
      } else if (typeof item.answer === 'string') {
        answerText = item.answer;
      }

      // Extract feedback from single object format (or null)
      let isLike: boolean | null = null;
      let comment = '';
      if (item.feedback && typeof item.feedback === 'object' && !Array.isArray(item.feedback)) {
        const feedbackData = item.feedback.feedback_data;
        if (feedbackData) {
          isLike = feedbackData.like ?? feedbackData.is_like ?? null;
          comment = feedbackData.comments || feedbackData.comment || '';
        }
      }

      let sourceMetadata = item.answer?.source_metadata || item.source_metadata || {
        follow_up_questions: [],
        references: {}
      };
      let metadata: Record<string, unknown> = {
        follow_up_questions: [],
        answer: {}
      };

      if (isRegulationsAgent(agentId)) {
        const answerMetadata = item.answer && typeof item.answer === 'object'
          ? asSavedAnswerMetadata(item.answer.metadata)
          : undefined;
        const followUpQuestions = sourceMetadata.follow_up_questions
          || answerMetadata?.follow_up_questions
          || [];
        const references = hasSourceReferences(sourceMetadata.references)
          ? sourceMetadata.references
          : citationsToNumberedMetadata(answerMetadata?.citations);
        sourceMetadata = { ...sourceMetadata, follow_up_questions: followUpQuestions, references };
        metadata = {
          follow_up_questions: followUpQuestions,
          answer: { metadata: references },
          context: answerMetadata?.context,
          active_identifiers: answerMetadata?.active_identifiers
        };
      }

      return {
        pk_chathistoryid: item.message_id || '',
        response_id: item.message_id || '',
        question: item.question || '',
        answer: answerText,
        created_at: item.agent_response_created_at || item.agent_response_updated_at || '',
        metadata: JSON.stringify(metadata),
        source_metadata: sourceMetadata,
        response_metadata: item.response_metadata,
        module_name: 'ria',
        is_like: isLike,
        feedback: item.feedback,
        comment
      };
    });

    return {
      status: 200,
      body: {
        thread_name: response.thread_name || '',
        data: transformedData.reverse()
      }
    };
  }

  /**
   * Transform Via agent response to expected format
   * 
   * Via agent response structure:
   * {
   *   status: "success",
   *   data: {
   *     responses: [{ response_id, question, answer: {text}, updated_at, is_like, comment }],
   *     thread_name: "..."
   *   }
   * }
   * 
   * @param response - Via agent response
   * @returns Transformed response in expected format
   */
  private transformPacAgentResponse(response: any): any {
    const transformedData = response.data.responses.map((item: any) => {
      // Extract answer text from answer object
      const answerText = item.answer?.text || item.answer || '';
      
      return {
        pk_chathistoryid: item.response_id,
        response_id: item.response_id,
        question: item.question,
        answer: answerText,
        created_at: item.updated_at,
        metadata: JSON.stringify({
          follow_up_questions: [],
          answer: {}
        }),
        source_metadata: {
          follow_up_questions: [],
          references: []
        },
        module_name: 'pac',
        is_like: item.is_like,
        comment: item.comment
      };
    });

    return {
      status: 200,
      body: {
        thread_name: response.data.thread_name || '',
        data: transformedData
      }
    };
  }

  /**
   * Transform generic agent response to expected format
   * For future agents with similar structure to Via agent
   * 
   * @param response - Generic agent response
   * @returns Transformed response in expected format
   */
  private transformGenericAgentResponse(response: any): any {
    const transformedData = response.data.responses.map((item: any) => {
      // Extract answer text - handle both object and string formats
      let answerText = '';
      if (typeof item.answer === 'object' && item.answer?.text) {
        answerText = item.answer.text;
      } else if (typeof item.answer === 'string') {
        answerText = item.answer;
      }
      
      return {
        pk_chathistoryid: item.response_id || item.id,
        response_id: item.response_id || item.id,
        question: item.question,
        answer: answerText,
        created_at: item.updated_at || item.created_at,
        metadata: item.metadata || JSON.stringify({
          follow_up_questions: [],
          answer: {}
        }),
        source_metadata: item.answer?.source_metadata || item.source_metadata || {
          follow_up_questions: [],
          references: {}
        },
        response_metadata: item.response_metadata,
        module_name: item.module_name || 'unknown',
        is_like: item.is_like,
        comment: item.comment
      };
    });

    return {
      status: 200,
      body: {
        thread_name: response.data.thread_name || response.thread_name || '',
        data: transformedData
      }
    };
  }
}
